import { type FormEvent, useEffect, useId, useState } from "react";
import { ApiError, type Ask, api, type Person, type TripView } from "./api.ts";
import { ago } from "./format.ts";
import { useJustDone } from "./justDone.ts";
import { useKept as useKeptList } from "./kept.ts";
import { useKept, useOnline } from "./offline.ts";
import { litProps } from "./opened.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { Avatar, Button, Chip, Icon } from "./souvenir.ts";
import { Count, FoldLine } from "./ui.tsx";

// Questions people put to each other, waiting until each person answers. A question waiting on me is answered here
// (DESIGN.md, Views, the web's own edits; features/asks.md, "Answer on the web"): a tap on one of its choices
// answers at once, a line of my own goes with Send, and while it's open my answer changes the same way. The same
// answer an AI would write, signed "on the web", undoable at once (the trip's notice) and from Updates. The page
// shows the question, the answers so far, and who it still waits on.

const nameIn = (p: { id: string; name: string }, you: string) => (p.id === you ? "you" : p.name);
const photo = (p: Person) => (p.src ? { src: p.src } : {});

function list(names: string[]): string {
  return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

// "Mei asks you", "You asked Ben and Mei", "Mei asks everyone".
function heading(ask: Ask, you: string): string {
  const mine = ask.by.id === you;
  const whom = ask.to === "everyone" ? "everyone" : list(ask.to.map((p) => nameIn(p, you)));
  return mine ? `You asked ${whom}` : `${ask.by.name} asks ${whom}`;
}

// Whether I'm one of the people asked (answered or not): everyone in the trip but its author, or those named.
export const askedOf = (ask: Ask, you: string) =>
  ask.to === "everyone" ? ask.by.id !== you : ask.to.some((p) => p.id === you);

export function AskCard({
  ask,
  trip,
  onOpen,
  onChanged,
  lit = false,
}: {
  ask: Ask;
  trip: TripView;
  // Opens what it's about, over the view it's in; left out where that's already on screen.
  onOpen?: (id: string) => void;
  // After I answer: load the trip again.
  onChanged: () => void;
  // What it's about was the last thing opened from here: lit.
  lit?: boolean;
}) {
  const person = (p: { id: string; name: string }): Person =>
    trip.members.find((m) => m.id === p.id) ?? { id: p.id, name: p.name, color: "sky" };
  // Nobody's answer is waited on in a read-only trip: it takes none (features/paying.md, "Spots").
  const readOnly = useReadOnly();
  const waiting = readOnly ? [] : ask.waiting_on;
  const waitingOnYou = waiting.some((p) => p.id === trip.you);
  const others = waiting.filter((p) => p.id !== trip.you);
  // Mine to answer, or to change while it's open: asked of me, in a trip that can change.
  const answering = !readOnly && ask.status === "open" && askedOf(ask, trip.you);
  // Mine to close (features/asks.md, "Close my question on the web"), while it's open, in a trip that can change.
  const closing = !readOnly && ask.status === "open" && ask.by.id === trip.you;
  const mine = ask.answers.find((a) => a.by.id === trip.you) ?? null;
  const reply = useAnswer(ask, trip, mine, onChanged);
  // Others' lines; mine is in my field to change, while I can.
  const lines = ask.answers.filter((a) => a.text && !(answering && a.by.id === trip.you));
  const choseBy = (o: string) =>
    ask.answers.filter((a) => (reply.shown ? a.by.id !== trip.you : true) && a.choice === o);
  return (
    <article className={`tm-ask${waitingOnYou ? " is-yours" : ""}`} aria-label={ask.text}>
      <header className="tm-ask-head">
        <Avatar name={ask.by.name} color={person(ask.by).color} {...photo(person(ask.by))} size={28} />
        <span className="caption tm-muted">
          <span className="tm-note-who">{heading(ask, trip.you)}</span> · {ago(ask.at)}
        </span>
        {ask.status === "closed" && <Chip icon="lock">Closed</Chip>}
      </header>
      <p className="body tm-ask-text">{ask.text}</p>
      {ask.about && onOpen && (
        <button
          type="button"
          className={`tm-ask-about callout${lit ? " is-lit" : ""}`}
          {...litProps(lit)}
          onClick={() => onOpen(ask.about?.id as string)}
        >
          <Icon name="pin" size={16} />
          <span>{ask.about.title}</span>
        </button>
      )}
      {ask.options &&
        (answering ? (
          <ul className="tm-ask-options" aria-label="Your answer">
            {ask.options.map((o) => {
              const chose = choseBy(o).filter((a) => a.by.id !== trip.you);
              return (
                <li key={o}>
                  <Chip selected={reply.shown?.choice === o} onClick={() => reply.choose(o)}>
                    {chose.length ? `${o} · ${list(chose.map((a) => nameIn(a.by, trip.you)))}` : o}
                  </Chip>
                </li>
              );
            })}
          </ul>
        ) : (
          <ul className="tm-ask-options" aria-label="Choices">
            {ask.options.map((o) => {
              const chose = ask.answers.filter((a) => a.choice === o);
              return (
                <li key={o}>
                  <Chip {...(chose.length ? { tone: "sea" as const, icon: "check" as const } : {})}>
                    {chose.length ? `${o} · ${list(chose.map((a) => nameIn(a.by, trip.you)))}` : o}
                  </Chip>
                </li>
              );
            })}
          </ul>
        ))}
      {lines.length > 0 && (
        <ul className="tm-note-list">
          {lines.map((a) => (
            <li key={a.id} className="tm-note">
              <Avatar name={a.by.name} color={person(a.by).color} {...photo(person(a.by))} size={24} />
              <p className="callout tm-note-text">
                <span className="tm-note-who">{a.by.id === trip.you ? "You" : a.by.name}</span>
                {a.choice ? ` (${a.choice})` : ""}: {a.text}
              </p>
            </li>
          ))}
        </ul>
      )}
      {answering && <AnswerLine reply={reply} />}
      {closing ? (
        <CloseLine ask={ask} trip={trip} waiting={others} onChanged={onChanged} />
      ) : (
        others.length > 0 && <p className="caption tm-muted">Waiting on {list(others.map((p) => p.name))}.</p>
      )}
    </article>
  );
}

// Who it still waits on, and Close question, for the question's author: closed as an AI's close does, with whatever
// answers came; undoable at once (the trip's notice) and from Updates. Over the question as read: changed meanwhile
// (my AI changed its words), nothing is closed.
function CloseLine({
  ask,
  trip,
  waiting,
  onChanged,
}: {
  ask: Ask;
  trip: TripView;
  waiting: { name: string }[];
  onChanged: () => void;
}) {
  const say = useJustDone();
  const online = useOnline();
  const kept = useKept();
  const offline = !online || kept !== null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = async () => {
    if (busy) return;
    if (offline) {
      setError("No signal: closing it needs signal.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { change } = await api.closeAsk(trip.id, ask.id, ask.version);
      onChanged();
      if (change) say({ change, text: "Closed your question" });
    } catch (e) {
      setBusy(false);
      if (e instanceof ApiError && e.status === 409 && e.data["ask"]) {
        setError("The question changed while you had it open, so it wasn't closed. Read it again.");
        onChanged();
      } else setError(e instanceof Error ? e.message : "Couldn't close the question. Try again.");
    }
  };
  return (
    <div className="tm-ask-close">
      <p className="caption tm-muted tm-ask-close-line">
        {waiting.length > 0
          ? `Waiting on ${list(waiting.map((p) => p.name))}.`
          : ask.answers.length > 0
            ? "Everyone has answered."
            : "Nobody to wait on."}
      </p>
      <Button
        variant="quiet"
        size="sm"
        icon="lock"
        className="tm-ask-close-button"
        data-edit="close-ask"
        disabled={busy}
        onClick={close}
      >
        {busy ? "Closing…" : "Close question"}
      </Button>
      {error && (
        <p className="caption tm-error tm-ask-close-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </div>
  );
}

type Reply = ReturnType<typeof useAnswer>;

// My answer as it's being given: what it shows now (sent, or as it was), and sending a choice or a line. Each send
// is my whole answer as it's to be: a choice keeps my line, a line keeps my choice.
function useAnswer(ask: Ask, trip: TripView, mine: Ask["answers"][number] | null, onChanged: () => void) {
  const say = useJustDone();
  const online = useOnline();
  const kept = useKept();
  const offline = !online || kept !== null;
  const stored = mine ? { choice: mine.choice ?? null, text: mine.text ?? null } : null;
  // What I sent ("none": taken back), shown until the trip loads with it.
  const [sent, setSent] = useState<{ choice: string | null; text: string | null } | "none" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const storedKey = JSON.stringify(stored);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the stored answer, as it loads
  useEffect(() => {
    if (sent && JSON.stringify(sent === "none" ? null : sent) === storedKey) setSent(null);
  }, [storedKey]);
  const shown = sent === "none" ? null : (sent ?? stored);
  const whose = ask.by.id === trip.you ? "your" : `${ask.by.name}'s`;
  // My whole answer as it's to be, or null to take it back (the question waits on me again).
  const send = async (next: { choice: string | null; text: string | null } | null) => {
    if (busy) return;
    if (offline) {
      setError("No signal: answering needs signal.");
      return;
    }
    if (JSON.stringify(next) === JSON.stringify(shown)) return;
    setBusy(true);
    setError(null);
    setSent(next ?? "none");
    try {
      if (next) {
        const { change } = await api.answer(trip.id, ask.id, { version: ask.version, ...next });
        onChanged();
        if (change)
          say({ change, text: stored ? `Changed your answer to ${whose} question` : `Answered ${whose} question` });
      } else {
        const { change } = await api.takeBackAnswer(trip.id, ask.id, ask.version);
        onChanged();
        if (change) say({ change, text: `Took back your answer to ${whose} question` });
      }
    } catch (e) {
      setSent(null);
      if (e instanceof ApiError && e.status === 409 && e.data["ask"]) {
        setError(
          `${ask.by.id === trip.you ? "You" : ask.by.name} changed the question while you had it open, so your answer ${next ? "wasn't sent" : "wasn't taken back"}. Read it again.`,
        );
        onChanged();
      } else
        setError(
          e instanceof Error
            ? e.message
            : next
              ? "Couldn't send your answer. Try again."
              : "Couldn't take it back. Try again.",
        );
    } finally {
      setBusy(false);
    }
  };
  return {
    shown,
    busy,
    error,
    // A choice tapped: answered at once, my line kept. Tapped again, it stays my answer.
    choose: (choice: string) => send({ choice, text: shown?.text ?? null }),
    // Taken back: no answer of mine, the question waiting on me again.
    takeBack: () => send(null),
    // A line sent: my choice kept; emptied, only my choice is left (with no choice, nothing to send).
    write: (text: string) => {
      const line = text.trim() || null;
      if (!line && !shown?.choice) return;
      return send({ choice: shown?.choice ?? null, text: line });
    },
  };
}

// A line of my own, and Send: my line as it is, to change while the question's open.
function AnswerLine({ reply }: { reply: Reply }) {
  const id = useId();
  const [text, setText] = useState(reply.shown?.text ?? "");
  const now = reply.shown?.text ?? "";
  // My line as it loads (sent from here or by my AI), unless I'm writing.
  const [edited, setEdited] = useState(false);
  useEffect(() => {
    if (!edited) setText(now);
  }, [now, edited]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await reply.write(text);
    setEdited(false);
  };
  const unchanged = text.trim() === now.trim();
  return (
    <form className="tm-ask-reply" onSubmit={submit}>
      <label className="tm-hidden" htmlFor={id}>
        Your answer
      </label>
      <div className="tm-ask-reply-line">
        <input
          id={id}
          className="sv-field-input tm-ask-reply-input"
          value={text}
          maxLength={2000}
          autoComplete="off"
          enterKeyHint="send"
          placeholder={now ? "Your answer" : "Your answer, in a line"}
          onChange={(e) => {
            setText(e.target.value);
            setEdited(true);
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          type="submit"
          icon="send"
          className="tm-ask-reply-send"
          disabled={reply.busy || unchanged || (!text.trim() && !reply.shown?.choice)}
        >
          {reply.busy ? "Sending…" : "Send"}
        </Button>
      </div>
      {reply.shown && (
        <Button
          variant="quiet"
          size="sm"
          type="button"
          className="tm-start"
          disabled={reply.busy}
          onClick={() => {
            setEdited(false);
            reply.takeBack();
          }}
        >
          Take back my answer
        </Button>
      )}
      {reply.error && (
        <p className="caption tm-error" role="alert">
          {reply.error}
          <ReportThis />
        </p>
      )}
    </form>
  );
}

// The Updates tab's questions: those waiting on me first, then my own that are still open. What one is
// about opens from it (`at`: which question it was opened from), lit when it was the last opened there. On a
// read-only trip none waits on anyone, so they're one list, in the same order, uncounted.
export function Questions({
  asks,
  trip,
  onOpen,
  onChanged,
  lit,
}: {
  asks: Ask[];
  trip: TripView;
  onOpen: (at: string, id: string) => void;
  onChanged: () => void;
  lit: (at: string, id: string) => boolean;
}) {
  const card = (a: Ask) => (
    <AskCard
      key={a.id}
      ask={a}
      trip={trip}
      onOpen={(id) => onOpen(`ask-${a.id}`, id)}
      onChanged={onChanged}
      lit={a.about ? lit(`ask-${a.id}`, a.about.id) : false}
    />
  );
  const readOnly = useReadOnly();
  const waits = (a: Ask) => a.waiting_on.some((p) => p.id === trip.you);
  // Those waiting on me when the tab opened stay where they were once answered (open still, to change), rather
  // than leaving under my finger; next time the tab opens, they're gone from here (still on what they're about).
  const [waitedFirst] = useState(() => new Set(asks.filter(waits).map((a) => a.id)));
  // Likewise those I'd answered when the tab opened: an answer taken back leaves it where it was, under my finger.
  const answeredOf = (a: Ask) => a.status === "open" && askedOf(a, trip.you) && a.by.id !== trip.you;
  const [answeredFirst] = useState(() => new Set(asks.filter((a) => answeredOf(a) && !waits(a)).map((a) => a.id)));
  const forYou = asks.filter(
    (a) =>
      (waits(a) && !answeredFirst.has(a.id)) || (waitedFirst.has(a.id) && a.status === "open" && askedOf(a, trip.you)),
  );
  // Mine open when the tab opened stay where they were once closed (said Closed, with Undo in the trip's notice), rather
  // than leaving under my finger; next time the tab opens, they're gone.
  const [mineFirst] = useState(
    () => new Set(asks.filter((a) => a.by.id === trip.you && a.status === "open").map((a) => a.id)),
  );
  const yours = asks.filter((a) => a.by.id === trip.you && (a.status === "open" || mineFirst.has(a.id)));
  // What I've answered that's still open, to change while it is: folded to one line until opened (kept so).
  const answered = readOnly
    ? []
    : asks.filter((a) => answeredOf(a) && !forYou.includes(a) && (!waits(a) || answeredFirst.has(a.id)));
  const [answeredOpen, setAnsweredOpen] = useKeptList("updates-answered-open", false);
  if (forYou.length + yours.length + answered.length === 0) return null;
  return (
    <section className="tm-questions" aria-labelledby="questions-title">
      <h2 id="questions-title" className="title">
        Questions
      </h2>
      {readOnly && <div className="tm-questions-group">{[...forYou, ...yours].map(card)}</div>}
      {!readOnly && forYou.length > 0 && (
        <section className="tm-questions-group" aria-label="Waiting on you">
          <h3 className="eyebrow tm-muted">
            Waiting on you
            <Count n={forYou.filter(waits).length} />
          </h3>
          {forYou.map(card)}
        </section>
      )}
      {!readOnly && yours.length > 0 && (
        <section className="tm-questions-group" aria-label="Your questions">
          <h3 className="eyebrow tm-muted">
            Your questions
            <Count n={yours.filter((a) => a.status === "open").length} />
          </h3>
          {yours.map(card)}
        </section>
      )}
      {answered.length > 0 && (
        <section className="tm-questions-group tm-questions-answered" aria-label="You answered">
          <FoldLine open={answeredOpen} controls="questions-answered" onToggle={() => setAnsweredOpen(!answeredOpen)}>
            {answered.length === 1 ? "One you answered, still open" : `${answered.length} you answered, still open`}
          </FoldLine>
          {answeredOpen && (
            <div id="questions-answered" className="tm-questions-group">
              {answered.map(card)}
            </div>
          )}
        </section>
      )}
    </section>
  );
}
